import type CreativeSDK from '@cesdk/cesdk-js';
import { RGBAColor, RGBColor } from '@cesdk/cesdk-js';
import { GQLProductSideSideType } from '@theprintbar/graphql';
import {
	Pantone,
	WebRoutes,
	getClosestPantone,
	isDefined,
	pantoneToPixel,
	pantoneToRGBA,
	replaceSVGColours,
	svgToDataUrl,
	withDomain,
} from '@theprintbar/shared';
import { getPage, getUserAddedBlocksOnPage, isGraphicOrTextBlock, isImageBlock, isTextBlock } from './block';
import { getRecolourList, rgbColourToPixel, rgbToPixel } from './colours';
import { getMetaColours, getOriginalFill } from './metadata';

async function resetRecolour(editor: CreativeSDK, page: number): Promise<void> {
	try {
		const { block } = editor.engine;

		const content = block.getChildren(page);
		if (!content.length) {
			console.log('No content on page', page);
			return;
		}

		await Promise.all(
			content.map(async child => {
				const blockType = block.getType(child);
				if (!isGraphicOrTextBlock(blockType)) return;

				if (block.hasMetadata(child, 'originalFill')) {
					const fillString = block.getMetadata(child, 'originalFill');
					if (isTextBlock(blockType)) {
						block.setColor(child, 'fill/solid/color', JSON.parse(fillString));
					} else {
						const fill = block.getFill(child);
						const props = block.findAllProperties(fill);
						const srcSet = block.getSourceSet(fill, 'fill/image/sourceSet');
						if (props.includes('fill/color/value')) {
							block.setColor(fill, 'fill/color/value', JSON.parse(fillString));
						} else if (!!srcSet?.length) {
							block.setSourceSet(fill, 'fill/image/sourceSet', [
								{
									width: block.getWidth(child),
									height: block.getHeight(child),
									uri: fillString,
								},
							]);
							if (block.hasMetadata(child, 'colours')) {
								block.removeMetadata(child, 'colours');
							}
						}
					}
					block.removeMetadata(child, 'originalFill');
				}

				if (block.hasMetadata(child, 'originalStroke')) {
					const strokeString = block.getMetadata(child, 'originalStroke');
					block.setColor(child, 'stroke/color', JSON.parse(strokeString));
					block.removeMetadata(child, 'originalStroke');
				}
			}),
		);

		editor.engine.editor.addUndoStep();

		return;
	} catch (e) {
		console.error('Failed to reset recolour on page', page, e);
	}
}

export interface RecolourOptions {
	domain: string;
	editor: CreativeSDK;
	page?: number;
	colourList: Pantone[];
	replacements?: Record<string, Pantone>;
	neckOnly?: boolean;
}

async function recolourPage(options: RecolourOptions): Promise<void> {
	try {
		const { editor, page, colourList, replacements, neckOnly } = options;

		const pageToRecolour = page || editor.engine.scene.getCurrentPage();
		if (!pageToRecolour) {
			console.log('No page to recolour');
			return;
		}

		const { block } = editor.engine;

		const content = neckOnly
			? editor.engine.block.findAll().filter(b => editor.engine.block.hasMetadata(b, 'neckLabel'))
			: getUserAddedBlocksOnPage(editor, pageToRecolour);
		if (!content.length) {
			console.log('No content on page', pageToRecolour);
			return;
		}

		await Promise.all(
			content.map(async child => {
				const blockType = block.getType(child);

				//Raw Shapes and Text just need their fill colour changed
				if (isImageBlock(blockType, editor, child)) {
					const fill = block.getFill(child);

					const oldColours = block.hasMetadata(child, 'colours') ? block.getMetadata(child, 'colours') : undefined;
					const newColours = JSON.stringify(getRecolourList(colourList, replacements));
					block.setMetadata(child, 'colours', newColours);

					if (!oldColours || oldColours !== newColours) {
						await recolourImageBlock(child, fill, options);
					}
				} else if (isTextBlock(blockType)) {
					recolourTextBlock(child, options);
				} else {
					const fillColour = block.getFillSolidColor(child);
					let colour = getClosestPantone(rgbToPixel(fillColour), colourList);
					const replacementColour = replacements?.[colour.name] || replacements?.['*'];
					if (replacementColour) {
						const { r, g, b } = pantoneToRGBA(replacementColour);
						block.setFillSolidColor(child, r, g, b);
					}
				}

				const strokeColour = block.getColor(child, 'stroke/color') as RGBColor;
				let strokeRecolour = getClosestPantone(rgbColourToPixel(strokeColour), colourList);
				const replacementColour = replacements?.[strokeRecolour.name] || replacements?.['*'];
				if (!!replacementColour) {
					strokeRecolour = replacementColour;
					block.setColor(child, 'stroke/color', pantoneToRGBA(strokeRecolour));
				}
			}),
		);

		editor.engine.editor.addUndoStep();

		return;
	} catch (e) {
		console.error('Failed to recolour page', options.page, e);
	}
}

async function recolourTextBlock(child: number, { editor, replacements, colourList }: RecolourOptions): Promise<void> {
	try {
		const text = editor.engine.block.getString(child, 'text/text');
		const list = colourList;
		//Iterate over the characters in the text to get their colours
		text.split('').forEach((_, index) => {
			const textColours = editor.engine.block.getTextColors(child, index, index + 1) as RGBAColor[];
			const colour = getClosestPantone(rgbColourToPixel(textColours[0]), list);
			const replacementColour = replacements?.[colour.name] || replacements?.['*'];
			if (!!replacementColour) {
				editor.engine.block.setTextColor(child, pantoneToRGBA(replacementColour), index, index + 1);
			}
		});
	} catch (e) {
		console.error('Failed to recolour text block', child, e);
	}
}

async function recolourImageBlock(imageBlock: number, fill: number, options: RecolourOptions): Promise<void> {
	try {
		const { block } = options.editor.engine;

		if (!block.isValid(imageBlock)) return;

		const from = Object.keys(options.replacements || {})
			.map(name => options.colourList.find(c => c.name === name))
			.filter(isDefined);
		const to = Object.values(options.replacements || {});

		const src = getOriginalFill(options.editor, fill);
		//Check if the image is an SVG url or data url
		if (src.includes('.svg') || src.startsWith('data:image/svg+xml')) {
			//Fetch the SVG and check if it's a vector
			const svgResponse = await fetch(src);
			const svg = await svgResponse.text();

			//Fetch the SVG and check if it's a vector
			const newSVG = replaceSVGColours(svg, options.replacements, options.colourList);

			block.setSourceSet(fill, 'fill/image/sourceSet', [
				{
					width: block.getWidth(imageBlock),
					height: block.getHeight(imageBlock),
					uri: svgToDataUrl(newSVG),
				},
			]);
			return;
		}

		block.setSourceSet(fill, 'fill/image/sourceSet', [
			{
				width: block.getWidth(imageBlock),
				height: block.getHeight(imageBlock),
				uri: withDomain(
					WebRoutes.API.utils.recolour({
						src,
						colours: options.colourList.map(pantoneToPixel),
						from: from.map(p => pantoneToPixel(p!)),
						to: to.map(pantoneToPixel),
					}),
					options.domain,
				),
			},
		]);
	} catch (e) {
		console.log('Failed to recolour image block', imageBlock);
	}
}

async function recolourSVGBlocks(editor: CreativeSDK, domain: string): Promise<void> {
	try {
		const { block } = editor.engine;
		const page = editor.engine.scene.getCurrentPage();
		if (!page) return;

		const content = getUserAddedBlocksOnPage(editor, page);
		if (!content.length) {
			return;
		}

		await Promise.all(
			content.map(async child => {
				const blockType = block.getType(child);
				if (!isImageBlock(blockType, editor, child)) return;

				const fill = block.getFill(child);
				let src = getOriginalFill(editor, fill);
				if (src.endsWith('.svg') || src.startsWith('data:image/svg+xml')) {
					const svgResponse = await fetch(src);
					const svg = await svgResponse.text();
					//Fetch the SVG and check if it's a vector
					const colours = await getMetaColours(editor, child);
					const newSVG = replaceSVGColours(svg, {}, colours);
					src = svgToDataUrl(newSVG);
					block.setSourceSet(fill, 'fill/image/sourceSet', [
						{
							width: block.getWidth(child),
							height: block.getHeight(child),
							uri: src,
						},
					]);
				}
			}),
		);

		return;
	} catch (e) {
		console.error('Failed to recolour SVG blocks', e);
	}
}
async function recolourSide(side: GQLProductSideSideType, options: Omit<RecolourOptions, 'page'>): Promise<void> {
	const { editor } = options;
	const page = getPage(editor, side);
	await recolourPage({ ...options, page });
}

async function resetRecolourSide(editor: CreativeSDK, side: GQLProductSideSideType | string): Promise<void> {
	const page = getPage(editor, side as GQLProductSideSideType);
	await resetRecolour(editor, page);
}

export default {
	recolourPage,
	recolourSide,
	resetRecolour,
	resetRecolourSide,
	recolourImageBlock,
	recolourSVGBlocks,
};
